import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import type { MapboxMap } from '@polaris-customerweb/sharedcomponents.core';
import { ConsoleLogService } from '@polaris-customerweb/sharedcomponents.shared';
import { Waypoint } from '@polaris-customerweb/sharedcomponents.user-content';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { selectActivePublicWaypoint } from 'src/app/components/waypoint/waypoint.reducer';
import { AppState } from 'src/app/models/app-state';
import { MapExtents } from 'src/app/state/map/map.reducer';
import { selectIsShowingPublicWaypoints } from '../state/map/layer-visibility/layer-visibility.reducer';
import { MapContentMapService } from './map-content-map.service';
import { MapService } from './map.service';
import { RideTypeService } from './ride-type.service';
import { WaypointService } from './waypoint.service';

@Injectable({ providedIn: 'root' })
export class PublicWaypointMapService extends MapContentMapService {
    override minZoom = 10;

    constructor(
        consoleLogService: ConsoleLogService,
        mapService: MapService,
        private rideTypeService: RideTypeService,
        private store: Store<AppState>,
        private waypointService: WaypointService
    ) {
        super(consoleLogService, mapService);
        super.setSourceId('waypointspublic');
    }

    setSourceDataFromModel(
        mapboxMap: MapboxMap,
        publicWaypoints: Waypoint[]
    ): void {
        const geoJson = publicWaypoints
            ? this.waypointService.waypointsToGeoJson(publicWaypoints)
            : undefined;
        super.setSourceData(mapboxMap, geoJson);
    }

    getPublicWaypointsWhenMapExtentsChanges(
        mapExtents$: Observable<MapExtents>
    ): Observable<Waypoint[]> {
        let previousLat = Infinity;
        let previousLng = Infinity;
        let previousZoom = Infinity;
        let waypointsHash = '';

        const filteredMapExtents$ = mapExtents$.pipe(
            filter((mapExtents) => {
                // do not emit if extents are invalid
                if (!mapExtents || !mapExtents.bounds) {
                    return false;
                }

                // do not emit if extents hasn't changed more than 10%
                if (
                    Math.abs(mapExtents.center.lat - previousLat) <
                        mapExtents.bounds.heightInDegrees * 0.1 &&
                    Math.abs(mapExtents.center.lng - previousLng) <
                        mapExtents.bounds.widthInDegrees * 0.1 &&
                    Math.abs(mapExtents.zoom - previousZoom) <
                        previousZoom * 0.1
                ) {
                    return false;
                }

                previousLat = mapExtents.center.lat;
                previousLng = mapExtents.center.lng;
                previousZoom = mapExtents.zoom;
                return true;
            })
        );

        return combineLatest([
            this.store.pipe(select(selectIsShowingPublicWaypoints)),
            this.store.pipe(select(selectActivePublicWaypoint)),
            this.rideTypeService.rideType$,
            filteredMapExtents$,
        ]).pipe(
            switchMap(
                ([isShowing, activePublicWaypoint, rideType, mapExtents]) => {
                    if (!isShowing || mapExtents.zoom <= this.minZoom) {
                        const waypoints = activePublicWaypoint
                            ? [activePublicWaypoint]
                            : [];
                        return of(waypoints);
                    }

                    return this.waypointService
                        .getPublicWaypoints(mapExtents.bounds, rideType)
                        .pipe(
                            filter((waypoints) => {
                                // do not emit if waypoints haven't changed
                                const newHash = waypoints.reduce(
                                    (hash, waypoint) => {
                                        return (
                                            hash + waypoint.id.substring(0, 5)
                                        );
                                    },
                                    ''
                                );

                                if (newHash === waypointsHash) {
                                    return false;
                                }

                                waypointsHash = newHash;

                                return true;
                            }),
                            map((publicWaypointsInView) => {
                                if (!activePublicWaypoint) {
                                    return publicWaypointsInView;
                                }

                                return [
                                    ...publicWaypointsInView.filter(
                                        (waypoint) =>
                                            waypoint.id !==
                                            activePublicWaypoint.id
                                    ),
                                    activePublicWaypoint,
                                ];
                            })
                        );
                }
            )
        );
    }
}
